
import React, { BaseSyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useSelector } from 'react-redux';

import './ChangePasswordModal.scss';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import { selectControls } from '../../../redux/controls';
import { TextFormControl } from '../../TextFormControl/TextFormControl';
import Spinner from '../../Spinner/Spinner';
import { fetchChangePasswordAsync, ModalProps } from '../models';
import { selectEmailId } from '../../../redux/account';

function ChangePasswordModal({ open, onClose }: ModalProps): JSX.Element {

  const controls = useSelector(selectControls);
  const emailId = useSelector(selectEmailId);

  ReactModal.setAppElement('#root');

  const formEl = useRef<HTMLFormElement>(null);

  const [contentLabel, setContentLabel] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [pwMismatch, setPWMismatch] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [successful, setSuccessful] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>('');

  const submitFormHandler = useCallback((event: BaseSyntheticEvent, formEl: React.RefObject<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (formEl.current) {
      const formData = new FormData(formEl.current);
      const formProps = Object.fromEntries(formData);
      if (
        formProps['new-password'] !== formProps['confirm-new-password']
      ) {
        setPWMismatch(true);
        const pwEl = document.getElementById('change-password-modal-new-pw') as HTMLInputElement;
        if (pwEl) {
          pwEl.focus();
          pwEl.select();
        }
      } else {
        setPWMismatch(false);
        setSubmitted(true);
        setProcessing(true);
        fetchChangePasswordAsync(
          emailId ? emailId : '',
          formProps['password'] as string,
          formProps['new-password'] as string,
          setErrMsg,
          setProcessing,
          setSuccessful
        );
      }
    }
  }, [emailId]);

  const closeModalHandler = useCallback(() => {
    setErrMsg('');
    setSuccessful(false);
    setSubmitted(false);
    setProcessing(false);
    setPWMismatch(false);
    setIsValid(false);
    onClose(true);
  }, [onClose]);

  // fires on mount - sets headerText and bodyText based on windowType
  useEffect(() => {
    if (controls) {
      setContentLabel(controls?.ForgotHeader?.controlText);
    }
  }, [controls]);

  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal"
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          contentLabel={contentLabel}
        >
          {
            !submitted || processing  ?
              <>
                <form ref={formEl} onSubmit={(e) => submitFormHandler(e, formEl)} className='nqt-modal--container'>
                  { processing && <Spinner/> }
                  <div className="nqt-modal--header-group">
                    <header className='nqt-modal--title'>{contentLabel}</header>
                    <button className='nqt-modal--close-button'
                      type='button'
                      ref={(el) => !isValid && el?.focus()}
                      onClick={() => onClose(false)}>
                        
                      <DeleteIcon/>
                      <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                    </button>
                  </div>
                  <div className="nqt-modal--border">
                    <section className="nqt-change-password-modal--content container">
                      <div className="nqt-modal--form-controls container-fluid">
                        <div className="row row-cols-1 gy-3">
                          {
                            pwMismatch &&
                        <div className="col nqt-modal--error">
                          <span className="nqt-modal--error-message">
                            {
                              controls?.PasswordMustMatch?.controlText
                            }
                          </span>
                        </div>
                          }
                          <div className="col container-fluid">
                            <TextFormControl id='change-password-modal-pw'
                              autoComplete="current-password"
                              containerClass="nqt-modal--text-input password"
                              controlName='password'
                              controlType="password"
                              headerText={'Current Password'}
                              isValidCallback={() => setIsValid(!!formEl.current?.checkValidity())}
                              pattern="^(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=\D*\d)(?=[^-!@#$%^&*()_+=]*[-!@#$%^&*()_+=]).{8,50}$"
                              required={true}
                              setFocus={false}/>
                          </div>
                          <div className="col container-fluid">
                            <p className="nqt-change-password-modal--rules"
                              dangerouslySetInnerHTML={{__html: controls?.PasswordInstruct ? controls?.PasswordInstruct?.controlText : ''}}>
                            </p>
                          </div>
                          <div className="col container-fluid">
                            <TextFormControl id='change-password-modal-new-pw'
                              autoComplete="new-password"
                              containerClass="nqt-modal--text-input password"
                              controlName='new-password'
                              controlType="password"
                              headerText={controls?.NewPwdHeader?.controlText}
                              isValidCallback={() => setIsValid(!!formEl.current?.checkValidity())}
                              pattern="^(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=\D*\d)(?=[^-!@#$%^&*()_+=]*[-!@#$%^&*()_+=]).{8,50}$"
                              required={true}/>
                          </div>
                          <div className="col container-fluid">
                            <TextFormControl id='change-password-modal-confirm-new-pw'
                              autoComplete="new-password"
                              containerClass="nqt-modal--text-input"
                              controlName='confirm-new-password'
                              controlType="password"
                              headerText={controls?.LoginPwd2?.controlText}
                              isValidCallback={() => setIsValid(!!formEl.current?.checkValidity())}
                              pattern="^(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=\D*\d)(?=[^-!@#$%^&*()_+=]*[-!@#$%^&*()_+=]).{8,50}$"
                              required={true} />
                          </div>
                        </div>
                      </div>
                    </section>
                    <hr />
                    <div className="nqt-modal--buttons col-9 offset-3">
                      <button className="nqt-button--cancel col-4 offset-1"
                        type='button'
                        onClick={() => onClose(false)}>{controls?.ModalCancel?.controlText}</button>
                      <button className="nqt-button--submit col-6"
                        disabled={!isValid}
                        type="submit"
                        onClick={(e) => submitFormHandler(e, formEl)}>{controls?.LoginPUChangePassword?.controlText}</button>
                    </div>
                  </div>
                </form>
              </> :
              <div className='nqt-modal--container'>
                <div className="nqt-modal--header-group">
                  <header className='nqt-modal--title'>{contentLabel}</header>
                  <button className='nqt-modal--close-button'
                    ref={(el) => el?.focus()}
                    onClick={() => closeModalHandler()}>
                    <DeleteIcon/>
                    <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                  </button>
                </div>
                <div className="nqt-modal--border">
                  <section className="nqt-modal--content">
                    {
                      successful ?
                        <div className="nqt-modal--description">
                          {
                            controls?.ResetSuccess?.controlText
                          }
                        </div> :
                        <div className="nqt-modal--description">
                          {
                            errMsg ?
                            errMsg as string :
                              controls?.StandardErrorMsg?.controlText
                          }
                        </div>
                    }
                  </section>
                </div>
              </div>
          }
        </ReactModal>
      }
    </>
  );
}

export default ChangePasswordModal;